import { Languages, Smartphone, FileCheck2, Code2, Layers, RotateCcw, ExternalLink, Check } from "lucide-react";

const PROTOTYPE_URL = "/anumati-prototype.html";

const features = [
  { icon: Languages, title: "Multi-language templates", desc: "Consent notices in English plus all 22 Eighth Schedule languages. Beneficiaries pick their language, you get language-accurate proof." },
  { icon: Smartphone, title: "Verify your way", desc: "SMS OTP, WhatsApp OTP, or a signed image/PDF upload for beneficiaries without a phone. Each is an unambiguous affirmative action." },
  { icon: FileCheck2, title: "Tamper-evident audit trail", desc: "Immutable, hashed, timestamped consent artifacts. Your §6(10) burden of proof, solved." },
  { icon: Code2, title: "One API, any MIS", desc: "A single HTTP call from your MIS creates a consent request. Works with Frappe, custom apps, or a no-code redirect. Live in a day." },
  { icon: Layers, title: "Multi-programme, isolated", desc: "One account, many programmes, each with its own template, languages and API key. One dashboard oversees all of it." },
  { icon: RotateCcw, title: "Withdrawal & re-consent", desc: "Beneficiaries withdraw with one tap; a webhook tells your MIS to stop processing. Re-consent on template change, built in." },
];

export const Anumati = () => (
  <section id="anumati" className="py-24 bg-[#F7F7F8]">
    <div className="max-w-[1120px] mx-auto px-6">
      <div className="flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-12">
        <div className="flex-1">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#9E1B32] bg-[#FBEAEE] px-3 py-1 rounded-full">Our consent manager POC · Anumati</span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight">
            DPDP consent, <span className="bg-gradient-to-r from-[#9E1B32] to-[#F58220] bg-clip-text text-transparent">handled.</span>
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#5C5F6B] leading-relaxed max-w-[600px]">
            Anumati is plug-and-play consent infrastructure. Drop one API call into your MIS and every beneficiary sees a
            clear, multi-language consent notice, verifies it by OTP, and lands in a tamper-evident audit trail,
            automatically.
          </p>
        </div>
        <a
          href={PROTOTYPE_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="anumati-prototype-link"
          className="inline-flex items-center gap-2 self-start px-6 py-3.5 rounded-[10px] bg-[#9E1B32] text-white text-[15px] font-semibold shadow-[0_4px_12px_-2px_rgba(158,27,50,0.4)] hover:bg-[#7A1426] hover:-translate-y-px transition-[background-color,transform]"
        >
          View Prototype <ExternalLink size={16} />
        </a>
      </div>

      <div className="mt-10 flex flex-wrap gap-4 text-[13.5px] text-[#5C5F6B]">
        {["OTP or signed-upload consent", "22 Indian languages", "Data hosted in India", "Live in a day"].map((t) => (
          <span key={t} className="flex items-center gap-1.5 bg-white border border-[#E7E7EC] rounded-full px-3.5 py-1.5">
            <Check size={14} className="text-green-600" /> {t}
          </span>
        ))}
      </div>

      <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {features.map((f) => (
          <div key={f.title} className="rounded-[14px] bg-white border border-[#E7E7EC] p-7 hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:-translate-y-0.5 transition-[box-shadow,transform]" data-testid={`anumati-feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <span className="w-10 h-10 rounded-[10px] bg-[#FBEAEE] grid place-items-center text-[#9E1B32]">
              <f.icon size={19} />
            </span>
            <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
            <p className="mt-2 text-sm text-[#5C5F6B] leading-relaxed">{f.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-10 rounded-[14px] border border-[#E7E7EC] bg-white p-8 lg:p-10" data-testid="anumati-positioning">
        <h3 className="text-lg font-semibold">Purpose-built for the DPDP Act, 2023</h3>
        <p className="mt-2 text-[15px] text-[#5C5F6B] leading-relaxed max-w-[840px]">
          Anumati is a consent management platform for Data Fiduciaries. It helps your organisation obtain, record and
          prove verifiable consent, mapping directly to the Act's requirements for clear-language notice, purpose
          limitation, affirmative action, withdrawal and record-keeping. You remain the accountable Data Fiduciary; Anumati
          is your compliance infrastructure, with data hosted in India.
        </p>
      </div>
    </div>
  </section>
);

export default Anumati;
